import { useState, useEffect, useRef, useCallback } from 'react';
import { clipsAPI } from '../lib/api';
import { X, ChevronLeft, ChevronRight, Eye, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const ClipViewer = ({ clipGroups, initialUserIndex = 0, onClose, onClipDeleted }) => {
  const { user } = useAuth();
  const [userIndex, setUserIndex] = useState(initialUserIndex);
  const [clipIndex, setClipIndex] = useState(0);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const currentGroup = clipGroups[userIndex];
  const currentClip = currentGroup?.clips[clipIndex];
  const totalClips = currentGroup?.clips.length || 0;

  const goNext = useCallback(() => {
    if (clipIndex < totalClips - 1) {
      setClipIndex(prev => prev + 1);
    } else if (userIndex < clipGroups.length - 1) {
      setUserIndex(prev => prev + 1);
      setClipIndex(0);
    } else {
      onClose();
    }
  }, [clipIndex, totalClips, userIndex, clipGroups.length, onClose]);

  const goPrev = useCallback(() => {
    if (clipIndex > 0) {
      setClipIndex(prev => prev - 1);
    } else if (userIndex > 0) {
      setUserIndex(prev => prev - 1);
      setClipIndex(0);
    }
  }, [clipIndex, userIndex]);

  // Mark as viewed
  useEffect(() => {
    if (currentClip) {
      clipsAPI.view(currentClip.clip_id).catch(() => {});
    }
  }, [currentClip]);

  // Auto-advance for images (5 seconds)
  useEffect(() => {
    if (currentClip?.media_type === 'image') {
      timerRef.current = setTimeout(goNext, 5000);
      return () => clearTimeout(timerRef.current);
    }
  }, [currentClip, goNext]);

  // Handle video end
  const handleVideoEnd = () => goNext();

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev, onClose]);

  const handleDelete = async () => {
    if (!currentClip) return;
    try {
      await clipsAPI.delete(currentClip.clip_id);
      toast.success('Clip deleted');
      onClipDeleted?.();
      goNext();
    } catch { toast.error('Failed to delete'); }
  };

  if (!currentClip) return null;

  const timeAgo = (date) => {
    const mins = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ago`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 flex gap-1 p-2 z-10">
          {currentGroup.clips.map((_, i) => (
            <div key={i} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  i < clipIndex ? 'w-full bg-white' : i === clipIndex ? 'w-full bg-white animate-pulse' : 'w-0'
                }`}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-4 left-0 right-0 flex items-center justify-between px-4 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-600 overflow-hidden border-2 border-white">
              {currentGroup.user_photo ? (
                <img src={currentGroup.user_photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white text-sm font-bold">
                  {currentGroup.user_name?.charAt(0)}
                </div>
              )}
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{currentGroup.user_name}</p>
              <p className="text-white/60 text-xs">{timeAgo(currentClip.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {currentClip.user_id === user?.user_id && (
              <button onClick={handleDelete} className="text-white/70 hover:text-red-400 p-2">
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button onClick={onClose} className="text-white/70 hover:text-white p-2">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Media */}
        <div className="w-full h-full max-w-md mx-auto flex items-center justify-center">
          {currentClip.media_type === 'video' ? (
            <video
              ref={videoRef}
              src={currentClip.media_url}
              autoPlay
              playsInline
              onEnded={handleVideoEnd}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <img
              src={currentClip.media_url}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
          )}
        </div>

        {/* Caption */}
        {currentClip.caption && (
          <div className="absolute bottom-16 left-0 right-0 px-6 z-10">
            <p className="text-white text-sm bg-black/40 rounded-xl px-4 py-2 max-w-md mx-auto text-center">
              {currentClip.caption}
            </p>
          </div>
        )}

        {/* View count */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center z-10">
          <span className="text-white/50 text-xs flex items-center gap-1">
            <Eye className="w-3 h-3" /> {currentClip.views || 0} views
          </span>
        </div>

        {/* Tap zones */}
        <div className="absolute inset-0 flex z-[5]">
          <div className="w-1/3 h-full cursor-pointer" onClick={goPrev} />
          <div className="w-1/3 h-full" />
          <div className="w-1/3 h-full cursor-pointer" onClick={goNext} />
        </div>

        {/* Nav arrows (desktop) */}
        {userIndex > 0 && (
          <button
            onClick={() => { setUserIndex(prev => prev - 1); setClipIndex(0); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-2 z-10 hidden md:block"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
        )}
        {userIndex < clipGroups.length - 1 && (
          <button
            onClick={() => { setUserIndex(prev => prev + 1); setClipIndex(0); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-2 z-10 hidden md:block"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default ClipViewer;
